import { useEffect, useState } from "react";
import DashboardLayout from "@/components/layout/DashboardLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import api, { formatError } from "@/lib/api";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";

const roleColors = {
  tenant: "bg-blue-50 text-blue-700",
  admin: "bg-purple-50 text-purple-700",
  super_admin: "bg-red-50 text-red-700",
  finance: "bg-emerald-50 text-emerald-700",
  operations: "bg-amber-50 text-amber-700",
};

const statusColors = {
  active: "bg-emerald-50 text-emerald-700",
  suspended: "bg-red-50 text-red-700",
};

const roles = ["tenant", "admin", "finance", "operations", "super_admin"];

export default function AdminUsersPage() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchUsers = () => {
    api.get("/admin/users")
      .then((r) => setUsers(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  };

  useEffect(() => { fetchUsers(); }, []);

  const updateRole = async (userId, role) => {
    try {
      await api.put(`/admin/users/${userId}/role`, { role });
      toast.success(`Role updated to ${role}`);
      fetchUsers();
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail));
    }
  };

  const toggleStatus = async (userId, currentStatus) => {
    const newStatus = currentStatus === "active" ? "suspended" : "active";
    try {
      await api.put(`/admin/users/${userId}/status`, { status: newStatus });
      toast.success(`User ${newStatus}`);
      fetchUsers();
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail));
    }
  };

  const stats = {
    total: users.length,
    tenants: users.filter((u) => u.role === "tenant").length,
    staff: users.filter((u) => ["admin", "finance", "operations", "super_admin"].includes(u.role)).length,
    suspended: users.filter((u) => u.status === "suspended").length,
  };

  return (
    <DashboardLayout title="User Management">
      <div className="space-y-6" data-testid="admin-users-page">
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          {[
            { label: "Total Users", value: stats.total },
            { label: "Tenants", value: stats.tenants },
            { label: "Staff", value: stats.staff },
            { label: "Suspended", value: stats.suspended },
          ].map((s) => (
            <Card key={s.label} className="border-border/60">
              <CardContent className="pt-6 text-center">
                <div className="font-heading text-2xl font-bold text-[#0A2540]">{s.value}</div>
                <p className="text-xs text-muted-foreground">{s.label}</p>
              </CardContent>
            </Card>
          ))}
        </div>

        <Card className="border-border/60">
          <CardContent className="p-0">
            {loading ? (
              <div className="flex justify-center py-12"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead>Email</TableHead>
                    <TableHead>Role</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Joined</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {users.map((u) => (
                    <TableRow key={u._id} data-testid={`user-row-${u._id}`}>
                      <TableCell className="font-medium">{u.name || "—"}</TableCell>
                      <TableCell className="text-sm text-muted-foreground">{u.email}</TableCell>
                      <TableCell><Badge className={roleColors[u.role]}>{u.role}</Badge></TableCell>
                      <TableCell>
                        <Badge
                          className={`${statusColors[u.status]} cursor-pointer`}
                          onClick={() => toggleStatus(u._id, u.status)}
                          data-testid={`toggle-status-${u._id}`}
                        >
                          {u.status}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-sm text-muted-foreground">
                        {u.created_at ? new Date(u.created_at).toLocaleDateString() : "—"}
                      </TableCell>
                      <TableCell>
                        <Select onValueChange={(v) => updateRole(u._id, v)}>
                          <SelectTrigger className="h-8 w-[120px] text-xs" data-testid={`role-select-${u._id}`}>
                            <SelectValue placeholder={u.role} />
                          </SelectTrigger>
                          <SelectContent>
                            {roles.filter((r) => r !== u.role).map((r) => (
                              <SelectItem key={r} value={r} className="text-xs">{r}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
}
